import React, {useState, useMemo, useRef, useEffect} from "react";
import {useTranslation} from "react-i18next";
import {Space, Typography, Spin} from "antd";
import {WarningOutlined} from "@ant-design/icons";
import {useFetch} from "../../../../hooks/useFetch";
import stores from "../../../../store/stores";
import {H3Map} from "../../../H3Map/H3Map";
import {
    StyledModal,
    Container,
    ContentWrapper,
    PoolsListContainer,
    PoolsListTitle,
    SearchInput,
    PoolsList,
    MapContainer,
    LoadingOverlay,
    ModalTitle,
    ErrorMessage,
    WarningBadge
} from "./PoolsListModal.styles";
import PoolCard from "./PoolCard";
import EmptyState from "./EmptyState";
import AllPoolsCard from "./AllPoolsCard";

const poolColors = [
    "#FF6B6B",
    "#4ECDC4",
    "#45B7D1",
    "#96CEB4",
    "#FFEEAD",
    "#D4A5A5",
    "#9B59B6",
    "#3498DB",
    "#E67E22",
    "#1ABC9C"
];

const PoolsListModal = ({isModalOpen, onClose, city}) => {
    const mapRef = useRef(null);
    const [selectedPool, setSelectedPool] = useState(null);
    const [hoveredPoolId, setHoveredPoolId] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [mapOpacity, setMapOpacity] = useState(0);
    const [error, setError] = useState(null);
    const [loading, h3CellsData] = useFetch({
        fetchFn: stores.poolStore.getPoolH3Cells.bind(null, {city: city}),
        dependencies: [city],
        initialCall: true
    });

    const poolColorMap = useMemo(() => {
        if (!h3CellsData?.data) return {};
        return h3CellsData.data.reduce((acc, pool, index) => {
            acc[pool._id] = poolColors[index % poolColors.length];
            return acc;
        }, {});
    }, [h3CellsData?.data]);

    const filteredPools = useMemo(() => {
        if (!h3CellsData?.data) return [];
        const searchTermLower = searchTerm.toLowerCase();
        return h3CellsData.data.filter(pool => pool.name.toLowerCase().includes(searchTermLower));
    }, [h3CellsData?.data, searchTerm]);

    const initialHexagons = useMemo(() => {
        if (selectedPool) {
            return (
                selectedPool.h3Cells?.map(cell => ({
                    resolution: cell.resolution,
                    index: cell.index,
                    poolName: selectedPool.name,
                    color: poolColorMap[selectedPool._id]
                })) || []
            );
        }

        return (
            h3CellsData?.data?.reduce((acc, pool) => {
                const poolHexagons =
                    pool.h3Cells?.map(cell => ({
                        resolution: cell.resolution,
                        index: cell.index,
                        color: poolColorMap[pool._id]
                    })) || [];
                return [...acc, ...poolHexagons];
            }, []) || []
        );
    }, [selectedPool?.h3Cells, h3CellsData?.data, poolColorMap]);

    useEffect(() => {
        if (mapRef.current && city) {
            setTimeout(() => {
                mapRef.current?.panToCity(city);
                setTimeout(() => {
                    setMapOpacity(1);
                }, 10);
            }, 150);
        }
    }, [city, isModalOpen, selectedPool]);

    const handlePoolSelect = pool => {
        try {
            setSelectedPool(pool);
            if (pool) {
                setTimeout(() => {
                    const element = document.getElementById(`pool-card-${pool._id}`);
                    if (element) {
                        element.scrollIntoView({behavior: "smooth", block: "center"});
                    }
                }, 100);
            }
        } catch (err) {
            setError("Havuz seçimi sırasında bir hata oluştu");
            console.error("Pool selection error:", err);
        }
    };

    const handlePoolEdit = poolId => {
        window.open(`/pools/${poolId}`, "_blank");
    };

    return (
        <StyledModal
            title={
                <ModalTitle>
                    <span>
                        <span style={{textTransform: "capitalize"}}>{city}</span> Havuz Bölgeleri ve
                        H3 Hücreleri
                    </span>
                    {loading && <Spin size="small" />}
                </ModalTitle>
            }
            visible={isModalOpen}
            footer={null}
            onCancel={onClose}
            width={1600}
            className="pools-list-modal"
        >
            <Container direction="vertical" size="middle">
                {error && (
                    <ErrorMessage>
                        <WarningOutlined /> {error}
                    </ErrorMessage>
                )}
                <ContentWrapper>
                    <PoolsListContainer>
                        <div>
                            <PoolsListTitle level={4}>Havuz Listesi</PoolsListTitle>
                            <SearchInput
                                placeholder="Havuz ara..."
                                onChange={e => setSearchTerm(e.target.value)}
                                allowClear
                            />
                        </div>
                        <PoolsList>
                            <Space direction="vertical" style={{width: "100%"}} size="middle">
                                {filteredPools.length > 0 && !searchTerm && (
                                    <AllPoolsCard
                                        totalPools={filteredPools.length}
                                        isSelected={!selectedPool}
                                        onSelect={() => handlePoolSelect(null)}
                                    />
                                )}
                                {filteredPools.map(pool => (
                                    <PoolCard
                                        key={pool._id}
                                        pool={pool}
                                        isSelected={selectedPool?._id === pool._id}
                                        onSelect={handlePoolSelect}
                                        onEdit={handlePoolEdit}
                                        hoveredPoolId={hoveredPoolId}
                                        setHoveredPoolId={setHoveredPoolId}
                                        poolColor={poolColorMap[pool._id]}
                                    />
                                ))}
                                {filteredPools.length === 0 && !loading && (
                                    <EmptyState
                                        hasPools={h3CellsData?.data?.length > 0}
                                        city={city}
                                    />
                                )}
                            </Space>
                        </PoolsList>
                    </PoolsListContainer>
                    <MapContainer>
                        {loading && (
                            <LoadingOverlay>
                                <Spin size="large" />
                            </LoadingOverlay>
                        )}
                        {selectedPool && !selectedPool.h3Cells?.length && (
                            <WarningBadge>
                                <WarningOutlined /> {selectedPool.name} havuzunun H3 hücreleri eksik
                            </WarningBadge>
                        )}
                        <H3Map
                            ref={mapRef}
                            initialHexagons={initialHexagons}
                            isActionsEnabled={false}
                            onlyH3Resolutions={true}
                            initialCity={city}
                            showToolsPanel={filteredPools.length > 0}
                            isToolsPanelVisibleOnInitial={false}
                            style={{
                                height: "100%",
                                opacity: mapOpacity,
                                transition: "opacity 0.1s ease-in-out"
                            }}
                        />
                    </MapContainer>
                </ContentWrapper>
            </Container>
        </StyledModal>
    );
};

export default PoolsListModal;
